import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import {
  BazisMaterialHelper,
  CadModelHelper,
  CadModelMaterialType,
  CuttingAllMaterialBaseDialogComponent,
  CuttingAllMaterialBaseDialogData,
  CuttingButt,
  CuttingCadModelMaterialLink,
  CuttingFurnitureBaseDialogComponent,
  CuttingMatBaseFurniture,
  CuttingMaterial,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingSettings,
  ICuttingBaseMaterial,
  LegacyDialogsService,
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
  NgxLocalizedNumbersService,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { filter, finalize, takeUntil, tap } from 'rxjs/operators';

import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { MatBaseSlotsDialogComponent } from './mat-base-slots-dialog/mat-base-slots-dialog.component';
@Component({
  selector: 'cutting-cadmodel-materials-matching',
  templateUrl: './cadmodel-materials-matching.component.html',
  styleUrls: ['./cadmodel-materials-matching.component.scss'],
})
export class CadModelMaterialsMatchingComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<void> = new Subject();
  materialDisplayedColumns = ['name', 'thickness', 'link_name', 'btnlink'];
  buttDisplayedColumns = ['name', 'thickness', 'link_name', 'btnlink'];
  slotDisplayedColumns = ['name', 'link_name', 'btnlink'];
  fastenerDisplayedColumns = ['name', 'link_name', 'btnlink'];
  assemblyDisplayedColumns = ['name', 'link_name', 'btnlink'];

  materialButtBaseDialogRef: MatDialogRef<MaterialBaseButtsDialogComponent>;
  materialBaseDialogRef: MatDialogRef<CuttingMaterialBaseDialogComponent>;
  matBaseSlotsDialogRef: MatDialogRef<MatBaseSlotsDialogComponent>;
  matBaseFastenersDialogRef: MatDialogRef<CuttingFurnitureBaseDialogComponent>;

  dataSourceMaterials: MatTableDataSource<CuttingCadModelMaterialLink> = new MatTableDataSource([]);
  dataSourceButts: MatTableDataSource<CuttingCadModelMaterialLink> = new MatTableDataSource([]);
  dataSourceSlots: MatTableDataSource<CuttingCadModelMaterialLink> = new MatTableDataSource([]);
  dataSourceFasteners: MatTableDataSource<CuttingCadModelMaterialLink> = new MatTableDataSource([]);
  dataSourceAssembly: MatTableDataSource<CuttingCadModelMaterialLink> = new MatTableDataSource([]);

  originalCadModelMaterialLink: CuttingCadModelMaterialLink[] = [];

  uniqCadModelMaterialLink: CuttingCadModelMaterialLink[] = [];

  cuttingSettings: CuttingSettings;

  @Input() set cadModelMaterialLink(val: CuttingCadModelMaterialLink[]) {
    this.originalCadModelMaterialLink = val;
    this.uniqCadModelMaterialLink = _.uniqWith(val, (a, b) => _.isEqual(a, b));
  }

  get cadModelMaterialLink(): CuttingCadModelMaterialLink[] {
    return this.uniqCadModelMaterialLink;
  }

  constructor(
    public dialog: MatDialog,
    private localizedNumbersService: NgxLocalizedNumbersService,
    private dialogsService: LegacyDialogsService,
    private settingsService: UserSettingsService
  ) {
    this.getCuttingSettings();
  }

  ngOnInit(): void {
    this.fillDataSources();
  }

  public formatOriginMaterial(material: CuttingCadModelMaterialLink): string {
    return BazisMaterialHelper.formatMaterialName(material.originalMaterialFullName);
  }

  openMaterialButtBaseDialog(filterByThickness: number): Promise<CuttingButt | undefined> {
    this.materialButtBaseDialogRef = this.dialog.open(MaterialBaseButtsDialogComponent, {
      panelClass: 'material-base-butt-list-dialog',
      disableClose: false,
      width: '1000px',
      data: {
        filterByThickness: filterByThickness,
        showQuantityInStock: this.cuttingSettings.showQuantityInStock,
      } as MaterialBaseEdgeDialogData,
    });

    return new Promise((resolve, reject) => {
      this.materialButtBaseDialogRef.afterClosed().subscribe((result: CuttingButt | undefined) => {
        this.materialButtBaseDialogRef = null;
        resolve(result);
      }, reject);
    });
  }

  removeLinkedMaterial(item: CuttingCadModelMaterialLink): void {
    const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

    itemTwins.forEach(m => {
      CadModelHelper.setLinkedMaterial(m, null);
    });
  }

  async linkMaterial(item: CuttingCadModelMaterialLink): Promise<void> {
    const materials = await this.openMaterialBaseDialog();
    if (materials) {
      const material: CuttingMaterial = materials[0];

      const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

      if (material && item.materialThickness !== material.thickness) {
        this.dialogsService
          .confirm(
            $localize`Заменяемый материал имеет толщину отличную от толщины выбранного материала. Продолжить замену?`
          )
          .pipe(
            tap(result => {
              if (result) {
                itemTwins.forEach(m => {
                  CadModelHelper.setLinkedMaterial(m, material);
                });
              } else this.linkMaterial(item);
            })
          )
          .subscribe();
      } else {
        itemTwins.forEach(m => {
          CadModelHelper.setLinkedMaterial(m, material);
        });
      }
    }
  }

  async linkAssembly(item: CuttingCadModelMaterialLink): Promise<void> {
    const materials = await lastValueFrom(
      this.dialogsService.component<CuttingAllMaterialBaseDialogData, ICuttingBaseMaterial[]>(
        CuttingAllMaterialBaseDialogComponent,
        { multiple: false },
        1000
      )
    );

    if (materials) {
      const material = materials[0];

      const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

      itemTwins.forEach(m => {
        this.setLinkedAssembly(m, material);
      });
    }
  }

  openMaterialBaseDialog(): Promise<CuttingMaterial[] | undefined> {
    this.materialBaseDialogRef = this.dialog.open(CuttingMaterialBaseDialogComponent, {
      panelClass: 'material-base-material-list-dialog',
      disableClose: false,
      width: '1150px',
      data: {
        materialsToExclude: [],
        multiple: false,
        showPrices: this.cuttingSettings.estimateSettings.showMaterialsPrice,
        showQuantityInStock: this.cuttingSettings.showQuantityInStock,
      } satisfies CuttingMaterialBaseDialogData,
    });

    return new Promise((resolve, reject) => {
      this.materialBaseDialogRef.afterClosed().subscribe((result: CuttingMaterial[] | undefined) => {
        this.materialBaseDialogRef = null;
        resolve(result);
      }, reject);
    });
  }

  openFastenersBaseDialog(): Promise<CuttingMatBaseFurniture[] | undefined> {
    this.matBaseFastenersDialogRef = this.dialog.open(CuttingFurnitureBaseDialogComponent, {
      panelClass: 'material-base-furniture-list-dialog',
      disableClose: false,
      width: '1150px',
    });
    this.matBaseFastenersDialogRef.componentInstance.currencySymbol =
      this.localizedNumbersService.getCurrentLocaleDefinition().currency;
    this.matBaseFastenersDialogRef.componentInstance.showQuantityInStock = this.cuttingSettings.showQuantityInStock;
    this.matBaseFastenersDialogRef.componentInstance.showMaterialsPrice =
      this.cuttingSettings.estimateSettings.showMaterialsPrice;

    return new Promise((resolve, reject) => {
      this.matBaseFastenersDialogRef.afterClosed().subscribe((result: CuttingMatBaseFurniture[] | undefined) => {
        this.matBaseFastenersDialogRef = null;
        resolve(result);
      }, reject);
    });
  }

  linkSlot(item: CuttingCadModelMaterialLink): void {
    this.openMatBaseSlotsDialog().subscribe(s => {
      const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

      itemTwins.forEach(m => {
        this.setLinkedSlot(m, s);
      });
    });
  }

  removeLinkedSlot(item: CuttingCadModelMaterialLink): void {
    const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

    itemTwins.forEach(m => {
      this.setLinkedSlot(m, null);
    });
  }

  private setLinkedSlot(source: CuttingCadModelMaterialLink, target: string): void {
    source.linkedMaterialId = 0;
    source.linkedMaterialName = target ?? '';
    source.linkedMaterialArticle = '';
    source.linkedMaterialThickness = 0;
    source.linkedMaterialType = CadModelMaterialType.Slot;
    source.linkedGroupMaterialId = 0;
    source.height = 0;
    source.width = 0;
  }

  async linkFastener(item: CuttingCadModelMaterialLink): Promise<void> {
    const fasteners = await this.openFastenersBaseDialog();

    if (fasteners) {
      const fastener = fasteners[0];

      const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

      itemTwins.forEach(m => {
        this.setLinkedFastener(m, fastener);
      });
    }
  }

  removeLinkedFastener(item: CuttingCadModelMaterialLink): void {
    const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

    itemTwins.forEach(m => {
      this.setLinkedFastener(m, null);
    });
  }

  removeLinkedAssembly(item: CuttingCadModelMaterialLink): void {
    const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

    itemTwins.forEach(m => {
      CadModelHelper.setLinkedMaterial(m, null);
    });
  }

  private setLinkedFastener(source: CuttingCadModelMaterialLink, target: CuttingMatBaseFurniture): void {
    source.linkedMaterialName = target?.name ?? '';
    source.linkedMaterialArticle = target?.article ?? '';
    source.linkedMaterialType = CadModelMaterialType.Fastener;
  }

  openMatBaseSlotsDialog(): Observable<string> {
    this.matBaseSlotsDialogRef = this.dialog.open(MatBaseSlotsDialogComponent, {
      panelClass: 'material-base-slot-list-dialog',
      disableClose: false,
      width: '500px',
    });

    return this.matBaseSlotsDialogRef.afterClosed().pipe(
      finalize(() => (this.matBaseSlotsDialogRef = null)),
      filter(res => res)
    );
  }

  async linkButt(item: CuttingCadModelMaterialLink): Promise<void> {
    const newButtFromBM = await this.openMaterialButtBaseDialog(item.materialThickness);

    if (newButtFromBM) {
      const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

      itemTwins.forEach(m => {
        this.setLinkedButt(m, newButtFromBM);
      });
    }
  }

  removeLinkedButt(item: CuttingCadModelMaterialLink): void {
    const itemTwins = this.originalCadModelMaterialLink.filter(m => _.isEqual(m, item));

    itemTwins.forEach(m => {
      this.setLinkedButt(m, null);
    });
  }

  private setLinkedButt(source: CuttingCadModelMaterialLink, target: CuttingButt): void {
    source.linkedMaterialId = target?.id ?? 0;
    source.linkedMaterialName = target?.name ?? '';
    source.linkedMaterialArticle = target?.article ?? '';
    source.linkedMaterialThickness = target?.thickness ?? 0;
    source.linkedButtSign = target?.sign ?? '';
    source.width = target?.width ?? 0;
    source.linkedButtAllowance = target?.allowance ?? 0;
    source.linkedButtOverhung = target?.overhang ?? 0;
    source.linkedMaterialType = CadModelMaterialType.Side;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private fillDataSources(): void {
    this.cadModelMaterialLink.forEach(linkMaterial => {
      switch (linkMaterial.materialType) {
        case CadModelMaterialType.Side:
          this.dataSourceButts.data.push(linkMaterial);
          break;

        case CadModelMaterialType.Plate:
        case CadModelMaterialType.Linear:
          this.dataSourceMaterials.data.push(linkMaterial);
          break;

        case CadModelMaterialType.Slot:
          this.dataSourceSlots.data.push(linkMaterial);
          break;

        case CadModelMaterialType.Fastener:
          this.dataSourceFasteners.data.push(linkMaterial);
          break;

        case CadModelMaterialType.Assembly:
          this.dataSourceAssembly.data.push(linkMaterial);
          break;
      }
    });
  }

  private setLinkedAssembly(source: CuttingCadModelMaterialLink, target: ICuttingBaseMaterial): void {
    source.linkedMaterialId = target?.id ?? 0;
    source.linkedGroupMaterialId = target?.groupId ?? 0;
    source.linkedMaterialName = target?.name ?? '';
    source.linkedMaterialArticle = target?.article ?? '';
    source.linkedMaterialType = CadModelMaterialType.Assembly;
  }

  private getCuttingSettings(): void {
    this.settingsService
      .getCuttingSettings()
      .pipe(
        tap(res => (this.cuttingSettings = res)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }
}
